import { ErrorResponse } from '../../../../../common/types/errorRespose';
import { CreateCustomRoleDto, CustomRoleDto } from '../../../../../api-client';
import React from 'react';
import { Box, Button, Flexbox, Icon, Modal, TextField, Typography } from '@hiiretail/synergy-ui';
import { ErrorBox } from '../../../../../common/components/ErrorBox/ErrorBox';
import { Controller } from 'react-hook-form';
import { useCreateCustomRoleForm } from '../../../hooks/useCreateCustomRoleForm';

export type CreateCustomRoleModalProps = {
  onClose: () => void;
  isOpen: boolean;
  isCreating: boolean;
  error: ErrorResponse | null;
  createCustomRole: (createCustomRoleDto: CreateCustomRoleDto) => void;
  selectedRole?: CustomRoleDto | null;
};

type CreateCustomRoleModalComponent = React.FC<CreateCustomRoleModalProps>;

export const CreateCustomRoleModal: CreateCustomRoleModalComponent = ({
  onClose,
  isOpen,
  isCreating,
  error,
  createCustomRole,
  selectedRole,
}) => {
  const { control, handleSubmit, formState } = useCreateCustomRoleForm();

  const filterPermissions = (selectedRole?: CustomRoleDto | null) =>
    selectedRole?.permissions.map((p) => {
      delete p.alias;
      return p;
    });

  const onFormSubmit = (formData: CreateCustomRoleDto) => {
    createCustomRole({
      ...formData,
      permissions: filterPermissions(selectedRole) || [],
      fixedBindings: selectedRole?.fixedBindings || [],
    });
  };

  return (
    <Modal
      data-testid="create-custom-role-modal"
      title="Create custom role"
      open={isOpen}
      onClose={onClose}
      footer={
        <Flexbox container justifyContent="flex-end">
          <Button
            disabled={
              (formState?.errors && !!Object.entries(formState?.errors).length) ||
              Object.keys(formState?.dirtyFields).length < 2 ||
              isCreating
            }
            // @ts-ignore
            onClick={handleSubmit(onFormSubmit)}
            type="submit"
          >
            {isCreating ? 'Creating ...' : 'Create'}
          </Button>
        </Flexbox>
      }
    >
      <ErrorBox error={error} />
      <Flexbox container gutter={4} alignItems="flex-start">
        <Flexbox item>
          <Icon.Info size="lg" color={() => '#6E33A9'} />
        </Flexbox>
        <Flexbox item container>
          <Typography variant="body2">
            A Custom Role can be created when pre-defined Roles are not sufficient for granting the
            required permissions for performing the responsibilities of a particular function.
            <br />
            This custom role is available{' '}
            <strong>only to the Hii Retail Tenant wherein it was created</strong>.
          </Typography>
        </Flexbox>
      </Flexbox>
      <Box mt={4} />
      <form>
        <Box mb={2}>
          <Controller
            name="id"
            defaultValue=""
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                label="ID"
                name="id"
                type="text"
                // @ts-ignore
                hint={
                  <span>
                    Unique role identifier
                    <br />
                    Must contain a-z letters, hyphens, underscores, dots and numbers.
                    <br />
                    Must be between 3 and 256 characters long
                  </span>
                }
                ref={ref}
                value={value}
                onChange={onChange}
                error={formState.errors?.id?.message}
              />
            )}
          />
        </Box>

        <Box mb={2}>
          <Controller
            name="name"
            defaultValue=""
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                label="Description"
                name="name"
                type="text"
                // @ts-ignore
                hint={
                  <span>
                    Explains what the custom role enables users to do.
                    <br />
                    Ie: "Can update deliveries"
                    <br />
                    Can not be longer than 256 characters
                  </span>
                }
                ref={ref}
                value={value}
                onChange={onChange}
                error={formState.errors?.name?.message}
              />
            )}
          />
        </Box>
      </form>
    </Modal>
  );
};
